/** Settings → Projects: merge a duplicate project into the one to keep, side by side with tick boxes. */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { GitMerge, Loader2, ArrowLeftRight, Search, Check, AlertTriangle, History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';

const gbp = (p) => (p == null ? '' : `£${(p / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const uk = (d) => (d ? d.split('-').reverse().join('/') : '');
const addr = (p) => [p.address_line1, p.town_city, p.postcode].filter(Boolean).join(', ');

function ProjectPick({ label, value, onChange, projects, exclude, testid, tone }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const sel = projects.find(p => p.id === value);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return projects.filter(p => p.id !== exclude && t.every(x => `${p.project_ref} ${p.client_name || ''} ${addr(p)} ${p.project_code || ''}`.toLowerCase().includes(x))).slice(0, 40);
  }, [projects, q, exclude]);
  return (
    <div className={`rounded-lg border p-3 ${tone}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-1.5">{label}</div>
      {sel && !open ? (
        <button type="button" onClick={() => setOpen(true)} className="w-full text-left" data-testid={`${testid}-selected`}>
          <div className="font-semibold text-white">{sel.project_ref}</div>
          <div className="text-xs text-gray-400">{sel.client_name || '—'} · {addr(sel) || 'no address'} · {sel.status}</div>
          <div className="text-[11px] text-gray-500 mt-1">Created {uk((sel.created_at || '').slice(0, 10))}{sel.created_by_name ? ` by ${sel.created_by_name}` : ''} · click to change</div>
        </button>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-500" />
            <Input autoFocus={open} value={q} onChange={e => setQ(e.target.value)} placeholder="Search ref, client, address…" className="pl-8 bg-[#1E2530] border-white/10 text-white" data-testid={`${testid}-search`} />
          </div>
          <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-white/5 rounded border border-white/10">
            {list.map(p => (
              <button key={p.id} type="button" onClick={() => { onChange(p.id); setOpen(false); setQ(''); }} className="w-full text-left px-3 py-2 hover:bg-white/5" data-testid={`${testid}-option-${p.id}`}>
                <div className="text-sm text-white">{p.project_ref}</div>
                <div className="text-[11px] text-gray-400">{p.client_name || '—'} · {addr(p)}</div>
              </button>
            ))}
            {!list.length && <div className="px-3 py-3 text-xs text-gray-500">No matches</div>}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ r, checked, onToggle, checkable, side }) {
  return (
    <label className={`flex items-start gap-2 px-2 py-1.5 rounded text-xs ${checkable ? 'cursor-pointer hover:bg-white/5' : ''} ${checked === false ? 'opacity-50 line-through decoration-gray-500' : ''}`} data-testid={`merge-row-${side}-${r.id}`}>
      {checkable && <Checkbox checked={checked !== false} onCheckedChange={onToggle} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]" data-testid={`merge-check-${side}-${r.id}`} />}
      <span className="text-gray-500 w-[72px] shrink-0 tabular-nums">{uk(r.when)}</span>
      <span className="flex-1 min-w-0">
        <span className="text-gray-200">{r.title}</span>
        {r.sub && <span className="block text-gray-500 truncate">{r.sub}</span>}
      </span>
      {r.amount_pence != null && <span className="tabular-nums text-gray-200 shrink-0">{gbp(r.amount_pence)}</span>}
      {r.status && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/15 text-gray-400 shrink-0">{r.status}</span>}
    </label>
  );
}

function Section({ s, discard, setDiscard }) {
  const toggle = (id) => setDiscard(d => ({ ...d, [s.key]: d[s.key]?.includes(id) ? d[s.key].filter(x => x !== id) : [...(d[s.key] || []), id] }));
  const dropped = discard[s.key] || [];
  const col = (rows, side) => (
    <div className="min-w-0">
      {rows.length === 0 ? <div className="px-2 py-2 text-xs text-gray-600">— none —</div> : rows.map(r => (
        <Row key={r.id} r={r} side={side} checkable={s.checkable} checked={!dropped.includes(r.id)} onToggle={() => toggle(r.id)} />
      ))}
    </div>
  );
  return (
    <div className="rounded-lg border border-white/10 overflow-hidden" data-testid={`merge-section-${s.key}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-white/[0.04] text-sm">
        <span className="font-medium text-white">{s.label} <span className="text-gray-500 text-xs">· keep {s.keep.length} · duplicate {s.dup.length}</span></span>
        {!s.checkable && <span className="text-[11px] text-gray-500">follow their lines — always move</span>}
        {s.checkable && dropped.length > 0 && <span className="text-[11px] text-amber-300">{dropped.length} unticked → {s.key === 'cost_ledger' ? 'voided' : 'left in the bin'}</span>}
      </div>
      <div className="grid grid-cols-2 divide-x divide-white/10">
        {col(s.keep, 'keep')}
        {col(s.dup, 'dup')}
      </div>
    </div>
  );
}

export function MergeProjectsTab({ api }) {
  const [projects, setProjects] = useState([]);
  const [keepId, setKeepId] = useState('');
  const [dupId, setDupId] = useState('');
  const [preview, setPreview] = useState(null);
  const [discard, setDiscard] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState('');
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);

  const load = async () => {
    const [c, h] = await Promise.all([api.get('/admin/projects/merge/candidates'), api.get('/admin/projects/merge/history')]);
    setProjects(c.data); setHistory(h.data);
  };
  useEffect(() => { load().catch(() => toast.error('Could not load projects')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPreview(null); setDiscard({}); }, [keepId, dupId]);

  const compare = async () => {
    setBusy(true); setResult(null);
    try { const r = await api.post('/admin/projects/merge/preview', { keep_id: keepId, dup_id: dupId }); setPreview(r.data); setDiscard({}); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not compare'); }
    finally { setBusy(false); }
  };
  const merge = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/projects/merge', { keep_id: keepId, dup_id: dupId, discard });
      setResult(r.data); setConfirm(false); setTyped(''); setPreview(null);
      toast.success(`Merged ${r.data.dup_ref} into ${r.data.keep_ref}`);
      setKeepId(''); setDupId('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
    finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    if (!preview) return null;
    let move = 0, drop = 0;
    preview.sections.forEach(s => { const d = discard[s.key] || []; move += s.dup.filter(r => !d.includes(r.id)).length + (s.checkable ? 0 : 0); drop += d.length; });
    return { move, drop };
  }, [preview, discard]);

  return (
    <div className="space-y-6" data-testid="merge-projects-tab">
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-500/10 rounded-md"><GitMerge className="w-5 h-5 text-sky-300" /></div>
            <div>
              <CardTitle className="text-white">Merge duplicate projects</CardTitle>
              <CardDescription className="text-gray-400">Pick the project to keep and the duplicate. Everything ticked on the duplicate moves across; untick anything to discard it (ledger rows are voided, other records stay in the Recycle bin with the duplicate). The kept project's details are left as they are and it learns the duplicate's job code.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
            <ProjectPick label="Keep this project" value={keepId} onChange={setKeepId} projects={projects} exclude={dupId} testid="merge-keep" tone="border-emerald-400/40 bg-emerald-500/5" />
            <button type="button" onClick={() => { const k = keepId; setKeepId(dupId); setDupId(k); }} disabled={!keepId && !dupId} className="self-center p-2 rounded-full border border-white/15 text-gray-300 hover:bg-white/10 disabled:opacity-40" title="Swap" data-testid="merge-swap"><ArrowLeftRight className="w-4 h-4" /></button>
            <ProjectPick label="Duplicate to merge in (goes to the Recycle bin)" value={dupId} onChange={setDupId} projects={projects} exclude={keepId} testid="merge-dup" tone="border-red-400/40 bg-red-500/5" />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={compare} disabled={!keepId || !dupId || busy} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#e0961c]" data-testid="merge-compare-btn">
              {busy && !preview ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />} Compare
            </Button>
            {preview && totals && <span className="text-xs text-gray-400" data-testid="merge-totals">{totals.move} record{totals.move === 1 ? '' : 's'} will move to <b className="text-white">{preview.keep.project_ref}</b>{totals.drop ? <> · <span className="text-amber-300">{totals.drop} unticked</span></> : null}</span>}
          </div>

          {preview && (
            <div className="space-y-3" data-testid="merge-preview">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/5 p-2.5"><div className="text-emerald-300 font-semibold">KEEP · {preview.keep.project_ref}</div><div className="text-gray-300">{preview.keep.client_name} · {addr(preview.keep)}</div></div>
                <div className="rounded-lg border border-red-400/40 bg-red-500/5 p-2.5"><div className="text-red-300 font-semibold">DUPLICATE · {preview.dup.project_ref}</div><div className="text-gray-300">{preview.dup.client_name} · {addr(preview.dup)}</div></div>
              </div>
              {preview.sections.length === 0 && <div className="text-sm text-gray-400">Neither project has any records yet — merging just removes the duplicate.</div>}
              {preview.sections.map(s => <Section key={s.key} s={s} discard={discard} setDiscard={setDiscard} />)}
              <div className="flex justify-end">
                <Button onClick={() => setConfirm(true)} disabled={busy} className="bg-sky-500 text-white hover:bg-sky-400" data-testid="merge-btn"><GitMerge className="w-4 h-4 mr-1.5" /> Merge {preview.dup.project_ref} into {preview.keep.project_ref}</Button>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3 text-sm text-emerald-100" data-testid="merge-result">
              <div className="font-semibold flex items-center gap-2"><Check className="w-4 h-4" /> Merged {result.dup_ref} into {result.keep_ref}</div>
              <div className="text-xs text-gray-300 mt-1">Moved: {Object.entries(result.moved).filter(([, n]) => n).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(', ') || 'nothing'}. Discarded: {Object.entries(result.discarded).filter(([, n]) => n).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(', ') || 'nothing'}. The duplicate is in the Recycle bin for 30 days.</div>
            </div>
          )}
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><History className="w-4 h-4 text-gray-400" /> Previous merges</CardTitle></CardHeader>
          <CardContent className="divide-y divide-white/5 text-xs">
            {history.map(h => (
              <div key={h.id} className="py-2 flex items-center gap-3" data-testid={`merge-history-${h.id}`}>
                <span className="text-gray-500 w-[80px]">{uk((h.created_at || '').slice(0, 10))}</span>
                <span className="text-gray-200 flex-1"><span className="line-through text-gray-500">{h.dup_ref}</span> → {h.keep_ref}</span>
                <span className="text-gray-500">{Object.values(h.moved || {}).reduce((a, b) => a + b, 0)} moved · {Object.values(h.discarded || {}).reduce((a, b) => a + b, 0)} discarded · {h.performed_by_name}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#1E2530] border-white/10 text-white" data-testid="merge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Merge {preview?.dup.project_ref} into {preview?.keep.project_ref}?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {totals?.move || 0} records move to {preview?.keep.project_ref}{totals?.drop ? `, ${totals.drop} unticked records are discarded (ledger rows voided, the rest parked in the bin)` : ''}. The duplicate goes to the Recycle bin; moved records stay on the kept project even if you restore it. Type the duplicate's reference to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder={preview?.dup.project_ref} className="bg-[#2A3240] border-white/10 text-white" data-testid="merge-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); merge(); }} disabled={busy || typed.trim().toLowerCase() !== (preview?.dup.project_ref || '').trim().toLowerCase()} className="bg-sky-500 text-white hover:bg-sky-400 disabled:opacity-50" data-testid="merge-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <GitMerge className="w-4 h-4 mr-1.5" />} Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
